/**
 * PendingInvitationsBanner Component
 * Shows pending project sharing invitations with Accept/Decline buttons.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Check, X, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui';
import type { SharedProject } from '@/lib/types';

interface PendingInvitationsBannerProps {
  invitations: SharedProject[];
  onAccept: (shareId: string, projectId: string) => Promise<void>;
  onDecline: (shareId: string, projectId: string) => Promise<void>;
}

export const PendingInvitationsBanner: React.FC<PendingInvitationsBannerProps> = ({
  invitations,
  onAccept,
  onDecline,
}) => {
  const { t } = useTranslation();
  const [loadingId, setLoadingId] = useState<string | null>(null);

  if (invitations.length === 0) return null;

  const handleAccept = async (shareId: string, projectId: string) => {
    setLoadingId(shareId + '-accept');
    try {
      await onAccept(shareId, projectId);
    } finally {
      setLoadingId(null);
    }
  };

  const handleDecline = async (shareId: string, projectId: string) => {
    setLoadingId(shareId + '-decline');
    try {
      await onDecline(shareId, projectId);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="mb-6 bg-indigo-50 border border-indigo-200 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="w-4 h-4 text-indigo-600" />
        <span className="text-sm font-semibold text-indigo-900">
          {t('projects.pendingInvitations', { count: invitations.length })}
        </span>
      </div>

      <div className="space-y-2">
        {invitations.map((inv) => (
          <div
            key={inv.shareId}
            className="flex items-center justify-between bg-white rounded-lg p-3 border border-indigo-100"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-900 truncate">
                {inv.projectName}
              </p>
              <p className="text-xs text-slate-500">
                {t('projects.invitationFrom', { email: inv.ownerEmail })}
                <Badge variant="default" size="sm" className="ml-2">
                  {inv.role === 'editor' ? t('projects.roleEditor') : t('projects.roleViewer')}
                </Badge>
              </p>
            </div>

            <div className="flex items-center gap-2 ml-3 flex-shrink-0">
              <button
                onClick={() => handleAccept(inv.shareId, inv.projectId)}
                disabled={loadingId !== null}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white
                         bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {loadingId === inv.shareId + '-accept' ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Check className="w-3.5 h-3.5" />
                )}
                {t('projects.accept')}
              </button>
              <button
                onClick={() => handleDecline(inv.shareId, inv.projectId)}
                disabled={loadingId !== null}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-600
                         bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
              >
                {loadingId === inv.shareId + '-decline' ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <X className="w-3.5 h-3.5" />
                )}
                {t('projects.decline')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PendingInvitationsBanner;
